import { useQuery, useSubscription } from '@apollo/client/react';

import { useReportDataLoading } from './useKitchenData';
import { useQueryErrorToast } from './useQueryErrorToast';
import type { NotificationReq } from '../components/Sidebar/Sidebar';
import {
  GetNotificationStatsQuery,
  GetNotificationStatsQueryVariables,
  KDSPermission,
  Kitchen,
  NotificationStats,
  NotificationType
} from '../libs';
import { GET_NOTIFICATION_STATS_QUERY, SUBSCRIBE_TO_UPDATE_NOTIFICATION_STATS_SUBSCRIPTION } from '../libs/graphQL';

interface UseNotificationStatsQueryProps {
  kitchen: string;
  date: string;
  skip: boolean;
}

const NOTIFICATION_PERMISSION: Record<NotificationType, KDSPermission> = {
  [NotificationType.logisticsChange]: KDSPermission.VIEW_LOGISTICS_CHANGES,
  [NotificationType.lockTimeChange]: KDSPermission.VIEW_LOCK_TIME_CHANGES,
  [NotificationType.shortage]: KDSPermission.VIEW_SHORTAGE_DISPLAY
};

export const canReceiveNotifications = (permissions: KDSPermission[]) =>
  Object.values(NOTIFICATION_PERMISSION).some((permission) => permissions.includes(permission));

export const toNotificationReq = (stats?: NotificationStats | null): NotificationReq => ({
  logisticsNotificationReq: {
    cxRequest: stats?.logisticsRequests?.cx || 0,
    customerRequest: stats?.logisticsRequests?.customer || 0
  },
  lockTimeNotificationReq: {
    cxRequest: stats?.kitchenRequests?.cx || 0,
    customerRequest: stats?.kitchenRequests?.customer || 0
  },
  shortagesNotificationReq: stats?.shortages?.kitchen || 0
});

/** The toasts to show: each count that grew since `shown`, the counts the toasts last ran against, for types the user may view. */
export const growingNotifications = (shown: NotificationReq, permissions: KDSPermission[], stats?: NotificationStats | null) => {
  const current = toNotificationReq(stats);
  return [
    {
      type: NotificationType.logisticsChange,
      count: current.logisticsNotificationReq.cxRequest,
      before: shown.logisticsNotificationReq.cxRequest
    },
    {
      type: NotificationType.lockTimeChange,
      count: current.lockTimeNotificationReq.cxRequest,
      before: shown.lockTimeNotificationReq.cxRequest
    },
    { type: NotificationType.shortage, count: current.shortagesNotificationReq, before: shown.shortagesNotificationReq }
  ]
    .filter(({ type, count, before }) => count > before && permissions.includes(NOTIFICATION_PERMISSION[type]))
    .map(({ type, count }) => ({ type, pendingReq: count }));
};

/** Owns the stats query: fetches it, keeps it live, and toasts its errors. Other screens read it via useNotificationRequests. */
const useNotificationStatsQuery = ({ kitchen, date, skip }: UseNotificationStatsQueryProps) => {
  const { data, error, loading, refetch } = useQuery<GetNotificationStatsQuery, GetNotificationStatsQueryVariables>(
    GET_NOTIFICATION_STATS_QUERY,
    {
      variables: { kitchen: kitchen as Kitchen, date },
      skip
    }
  );

  useQueryErrorToast(error, error && `Error fetching notification stats: ${error.message}`);
  useReportDataLoading(loading);

  // Keeps the default fetch policy: Apollo's own cache write of the payload is what updates the stats query above.
  useSubscription(SUBSCRIBE_TO_UPDATE_NOTIFICATION_STATS_SUBSCRIPTION, { variables: { kitchen }, ignoreResults: true, skip });

  return {
    notificationStats: data?.getNotificationStats as NotificationStats,
    notificationStatsLoading: loading,
    refetchNotificationStats: refetch
  };
};

/** The request counts for the kitchen and day, read from the cache useNotificationStatsQuery fills, with no request of its own. */
export const useNotificationRequests = (kitchen: string, date: string): NotificationReq => {
  const { data } = useQuery<GetNotificationStatsQuery, GetNotificationStatsQueryVariables>(GET_NOTIFICATION_STATS_QUERY, {
    variables: { kitchen: kitchen as Kitchen, date },
    fetchPolicy: 'cache-only'
  });
  return toNotificationReq(data?.getNotificationStats);
};

export default useNotificationStatsQuery;
